import React, { useState } from 'react';
import { X, User, Phone, Mail, MapPin, Building, DollarSign, MessageSquare, Send } from 'lucide-react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../utils/firebase';

const DealerButton: React.FC = () => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    city: '',
    state: '',
    businessType: '',
    experience: '',
    investment: '',
    message: ''
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      // Add document to Firestore
      await addDoc(collection(db, "dealership"), {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        city: formData.city,
        state: formData.state,
        businessType: formData.businessType,
        experience: formData.experience,
        investment: formData.investment,
        message: formData.message,
        time: serverTimestamp()
      });

      // Show success message
      alert('Thank you for your interest! We will contact you soon.');

      // Reset form and close modal
      setShowForm(false);
      setFormData({
        name: '',
        email: '',
        phone: '',
        city: '',
        state: '',
        businessType: '',
        experience: '',
        investment: '',
        message: ''
      });
    } catch (error) {
      console.error("Error submitting dealer form:", error);
      alert('There was an error submitting your form. Please try again.');
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;

    // Check if the field is required and contains only whitespace
    const requiredFields = ['name', 'email', 'phone', 'city', 'state', 'businessType', 'experience', 'investment'];
    if (requiredFields.includes(name) && value.trim() === '') {
      e.target.setCustomValidity('This field cannot be empty or contain only spaces');
    } else {
      e.target.setCustomValidity('');
    }

    setFormData({
      ...formData,
      [name]: value
    });
  };

  return (
    <>
      <div className="fixed bottom-8 right-8 z-50">
        <button
          className="bg-[#CC0000] hover:bg-[#AA0000] text-white px-6 py-3 rounded-full shadow-lg transition-all duration-300 hover:shadow-xl relative"
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
          onClick={() => setShowForm(true)}
        >
          Be a dealer now!
        </button>
        {showTooltip && !showForm && (
          <div className="absolute bottom-full mb-2 right-0 px-3 py-1.5 bg-black/90 text-white text-xs rounded whitespace-nowrap">
            Join our dealer network
            <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-x-4 border-x-transparent border-t-4 border-t-black/90"></div>
          </div>
        )}
      </div>

      {/* Dealer Inquiry Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="bg-gradient-to-r from-[#CC0000] to-[#AA0000] text-white p-6 rounded-t-2xl">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-2xl font-bold mb-2">Become a Dawki Mobility Dealer</h2>
                  <p className="text-white/90">Join India's leading electric mobility revolution</p>
                </div>
                <button
                  onClick={() => setShowForm(false)}
                  className="text-white hover:text-white/80 transition-colors duration-200"
                >
                  <X size={24} />
                </button>
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Personal Information */}
              <div>
                <h3 className="text-lg font-semibold text-[#1A1A1A] mb-4 flex items-center">
                  <User size={20} className="mr-2 text-[#CC0000]" />
                  Personal Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#CC0000] focus:border-transparent transition-all duration-200"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Enter your full name"
                    />
                  </div>
                  <div>
                    <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                      <Phone size={16} className="mr-1" />
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#CC0000] focus:border-transparent transition-all duration-200"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="+91 XXXXX XXXXX"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                      <Mail size={16} className="mr-1" />
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#CC0000] focus:border-transparent transition-all duration-200"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="your.email@example.com"
                    />
                  </div>
                </div>
              </div>

              {/* Location Information */}
              <div>
                <h3 className="text-lg font-semibold text-[#1A1A1A] mb-4 flex items-center">
                  <MapPin size={20} className="mr-2 text-[#CC0000]" />
                  Location Details
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      City *
                    </label>
                    <input
                      type="text"
                      name="city"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#CC0000] focus:border-transparent transition-all duration-200"
                      value={formData.city}
                      onChange={handleInputChange}
                      placeholder="Enter your city"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      State *
                    </label>
                    <select
                      name="state"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#CC0000] focus:border-transparent transition-all duration-200"
                      value={formData.state}
                      onChange={handleInputChange}
                    >
                      <option value="">Select State</option>
                      <option value="Andhra Pradesh">Andhra Pradesh</option>
                      <option value="Arunachal Pradesh">Arunachal Pradesh</option>
                      <option value="Assam">Assam</option>
                      <option value="Bihar">Bihar</option>
                      <option value="Chhattisgarh">Chhattisgarh</option>
                      <option value="Goa">Goa</option>
                      <option value="Gujarat">Gujarat</option>
                      <option value="Haryana">Haryana</option>
                      <option value="Himachal Pradesh">Himachal Pradesh</option>
                      <option value="Jharkhand">Jharkhand</option>
                      <option value="Karnataka">Karnataka</option>
                      <option value="Kerala">Kerala</option>
                      <option value="Madhya Pradesh">Madhya Pradesh</option>
                      <option value="Maharashtra">Maharashtra</option>
                      <option value="Manipur">Manipur</option>
                      <option value="Meghalaya">Meghalaya</option>
                      <option value="Mizoram">Mizoram</option>
                      <option value="Nagaland">Nagaland</option>
                      <option value="Odisha">Odisha</option>
                      <option value="Punjab">Punjab</option>
                      <option value="Rajasthan">Rajasthan</option>
                      <option value="Sikkim">Sikkim</option>
                      <option value="Tamil Nadu">Tamil Nadu</option>
                      <option value="Telangana">Telangana</option>
                      <option value="Tripura">Tripura</option>
                      <option value="Uttar Pradesh">Uttar Pradesh</option>
                      <option value="Uttarakhand">Uttarakhand</option>
                      <option value="West Bengal">West Bengal</option>
                      <option value="Delhi">Delhi</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Business Information */}
              <div>
                <h3 className="text-lg font-semibold text-[#1A1A1A] mb-4 flex items-center">
                  <Building size={20} className="mr-2 text-[#CC0000]" />
                  Business Details
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Business Type *
                    </label>
                    <select
                      name="businessType"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#CC0000] focus:border-transparent transition-all duration-200"
                      value={formData.businessType}
                      onChange={handleInputChange}
                    >
                      <option value="">Select Business Type</option>
                      <option value="New Business">New Business</option>
                      <option value="Existing Automotive Dealer">Existing Automotive Dealer</option>
                      <option value="Fleet Operator">Fleet Operator</option>
                      <option value="Service Center">Service Center</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Experience in Automotive *
                    </label>
                    <select
                      name="experience"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#CC0000] focus:border-transparent transition-all duration-200"
                      value={formData.experience}
                      onChange={handleInputChange}
                    >
                      <option value="">Select Experience</option>
                      <option value="No Experience">No Experience</option>
                      <option value="1-3 Years">1-3 Years</option>
                      <option value="3-5 Years">3-5 Years</option>
                      <option value="5-10 Years">5-10 Years</option>
                      <option value="10+ Years">10+ Years</option>
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                      <DollarSign size={16} className="mr-1" />
                      Investment Capacity *
                    </label>
                    <select
                      name="investment"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#CC0000] focus:border-transparent transition-all duration-200"
                      value={formData.investment}
                      onChange={handleInputChange}
                    >
                      <option value="">Select Investment Range</option>
                      <option value="₹5-10 Lakhs">₹5-10 Lakhs</option>
                      <option value="₹10-25 Lakhs">₹10-25 Lakhs</option>
                      <option value="₹25-50 Lakhs">₹25-50 Lakhs</option>
                      <option value="₹50 Lakhs - 1 Crore">₹50 Lakhs - 1 Crore</option>
                      <option value="₹1+ Crore">₹1+ Crore</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Additional Message */}
              <div>
                <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                  <MessageSquare size={16} className="mr-1" />
                  Additional Message
                </label>
                <textarea
                  name="message"
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#CC0000] focus:border-transparent transition-all duration-200"
                  value={formData.message}
                  onChange={handleInputChange}
                  placeholder="Tell us about your business goals, location preferences, or any specific requirements..."
                />
              </div>

              {/* Benefits Section */}
              <div className="bg-gradient-to-r from-[#CC0000]/5 to-[#CC0000]/10 rounded-lg p-4">
                <h4 className="font-semibold text-[#1A1A1A] mb-3">Dealer Benefits:</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-700">
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-[#CC0000] rounded-full mr-2"></div>
                    Exclusive territory rights
                  </div>
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-[#CC0000] rounded-full mr-2"></div>
                    Comprehensive training program
                  </div>
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-[#CC0000] rounded-full mr-2"></div>
                    Marketing & promotional support
                  </div>
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-[#CC0000] rounded-full mr-2"></div>
                    Technical support & service backup
                  </div>
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-[#CC0000] rounded-full mr-2"></div>
                    Attractive margin structure
                  </div>
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-[#CC0000] rounded-full mr-2"></div>
                    Flexible financing options
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex justify-end space-x-4 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-[#CC0000] hover:bg-[#AA0000] text-white font-medium py-3 px-8 rounded-lg transition-all duration-300 flex items-center hover:shadow-lg"
                >
                  <Send size={20} className="mr-2" />
                  Submit Inquiry
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default DealerButton;